import react from 'react';
import { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

// This key was created specifically for the demo in mui.com.
// You need to create a new one for your application.
const GOOGLE_MAPS_API_KEY = 'AIzaSyDqNnSuRDD3nyJ-oVOY4jV7McgqBIDyId0';

function loadScript(src, position, id) {
    if (!position) {
        return;
    }

    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('id', id);
    script.src = src;
    position.appendChild(script);
}

const autocompleteService = { current: null };
const placesService = { current: null };

export default function GoogleMapsAutocomplete(props) {
    const [value, setValue] = react.useState(props.value);
    const [inputValue, setInputValue] = react.useState('');
    const [options, setOptions] = react.useState([]);
    const loaded = react.useRef(false);
    const googleAttribution = useRef({});

    if (typeof window !== 'undefined' && !loaded.current) {
        if (!document.querySelector('#google-maps')) {
            loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
                document.querySelector('head'),
                'google-maps',
            );
        }

        loaded.current = true;
    }

    const fetch = react.useMemo(
        () =>
            throttle((request, callback) => {
                var googleRequest = {
                    input: request.input,
                    //bounds: defaultBounds,
                    types: ["geocode"],
                    componentRestrictions: { 'country': ['us'] },
                    fields: ['geometry', 'formatted_address']
                }
                autocompleteService.current.getPlacePredictions(request, callback);
            }, 200),
        [],
    );

    react.useEffect(() => {
        let active = true;

        if (!autocompleteService.current && window.google) {
            autocompleteService.current =
                new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        //if (props.value !== '') {
        //    setValue(props.value);
        //    setOptions(props.value ? [props.value] : []);
        //    return undefined;
        //}
        //else 
        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        fetch({ input: inputValue }, (results) => {
            if (active) {
                let newOptions = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    let michiganOnly = [];
                    for (var i = 0; i < results.length; i++) {
                        if (results[i].terms.length > 0) {
                            // find the State term
                            for (var j = 0; j < results[i].terms.length; j++) {
                                if (results[i].terms[j].value.length == 2) {
                                    if (results[i].terms[j].value.toUpperCase() == 'MI') {
                                        michiganOnly.push(results[i]);
                                    }
                                    console.log(results[i].terms[j].value);
                                }
                            }
                        }
                    }
                    newOptions = [...newOptions, ...michiganOnly];
                   // newOptions = [...newOptions, ...results];
                }

                setOptions(newOptions);
            }
        });

        

        return () => {
            active = false;
        };
    }, [value, inputValue, fetch]);

    function geoCodePlace(autoCompleteSelection) {

        if (placesService.current == null) {
            // $("body").append("<div id='GoogleAttribution'></div>");

            placesService.current = new google.maps.places.PlacesService(googleAttribution.current); //new google.maps.places.PlacesService(document.getElementById('GoogleAttribution'));
        }

        if (!placesService.current) {
            return undefined;
        }

        let request = {
            placeId: autocompleteSelection.placeId
        }

        placesService.getDetails(request, function (result, status) {
            if (result != null) {
                const place = result;
                if (!place.geometry) {
                    // User entered the name of a Place that was not suggested and
                    // pressed the Enter key, or the Place Details request failed.
                    //window.alert("No details available for input: '" + place.name + "'");
                    return;
                }
                else {
                    let latitude = place.geometry.location.lat();
                    let longitude = place.geometry.location.lng();

                    autoCompleteSelection.latitude = place.geometry.location.lat();
                    autoCompleteSelection.longitude = place.geometry.location.lng();
                    if (props.onSelected !== undefined) {
                        props.onSelected(autoCompleteSelection);
                    }
                }

                // try to split on a comma
                //let components = place.formatted_address.split(',');
                //if (components.length > 0) {
                //    $("#CustomerAddress_Street").val(components[0].trim());
                //    Global_CustomerAddress_Street = components[0].trim();
                //}
                //if (components.length > 1) {
                //    $("#CustomerAddress_City").val(components[1].trim());
                //    Global_CustomerAddress_City = components[1].trim();
                //}
            }
        });
    }

    return (
        <Autocomplete
            id="google-map-address"
            ref={googleAttribution }
            sx={{ width: 300 }}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
            }
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                
                // last 3 items in newValue.terms are Country, State, City. Put everything else as the Street
                let streetAddress = '';
                if (newValue !== null) {
                    if (newValue.terms.length > 3) {
                        for (let i = 0; i < newValue.terms.length - 3; i++) {
                            streetAddress += newValue.terms[i].value + " ";
                        }
                        streetAddress = streetAddress.trim();
                        setValue(streetAddress);
                    }
                    else {
                        setValue(newValue);
                    }
                }
                else {
                    setValue(newValue);
                }

                let geocoder = new google.maps.Geocoder();
                geocoder
                    .geocode({ placeId: newValue.place_id })
                    .then(({ results }) => {
                        if (results.length > 0) {
                            newValue.latitude = results[0].geometry.location.lat();
                            newValue.longitude = results[0].geometry.location.lng();
                            let postalCode = results[0].address_components.find(component => component.types.includes('postal_code'))?.long_name;
                            if (postalCode !== undefined && postalCode !== null) {
                                newValue.postalCode = postalCode;
                            }
                            if (props.onSelected !== undefined) {
                                props.onSelected(newValue);
                            }
                        }
                    })
                    .catch((e) => window.alert("Geocoder failed due to: " + e));


              
              
              
            }}
            onInputChange={(event, newInputValue) => {
                console.log('GoogleMapsAutocomplete - onInputChange: ' + newInputValue);
                setInputValue(newInputValue);
            }}
            renderInput={(params) => (
                <TextField {...params} label={props.label === undefined ? 'Street' : props.label} fullWidth />
            )}
            renderOption={(props, option) => {
                const matches = option.structured_formatting.main_text_matched_substrings;
                const parts = parse(
                    option.structured_formatting.main_text,
                    matches.map((match) => [match.offset, match.offset + match.length]),
                );

                return (
                    <li {...props}>
                        <Grid container alignItems="center">
                            <Grid item>
                                <Box
                                    component={LocationOnIcon}
                                    sx={{ color: 'text.secondary', mr: 2 }}
                                />
                            </Grid>
                            <Grid item xs>
                                {parts.map((part, index) => (
                                    <span
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                        }}
                                    >
                                        {part.text}
                                    </span>
                                ))}

                                <Typography variant="body2" color="text.secondary">
                                    {option.structured_formatting.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
}
